import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { getPrices } from '../services/api';
import Navbar from '../components/Navbar';
import Watchlist from '../components/Watchlist';
import StockSearch from '../components/StockSearch';
import Portfolio from '../components/Portfolio';
import TradePanel from '../components/TradePanel';
import StockChart from '../components/StockChart';
import PortfolioChart from '../components/PortfolioChart';
import CashModal from '../components/CashModal';
import TransactionHistory from '../components/TransactionHistory';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(1000);
  const [holdings, setHoldings] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [activeTab, setActiveTab] = useState('watchlist');
  const [stocksValue, setStocksValue] = useState(0);
  const [showCashModal, setShowCashModal] = useState(false);

  // Fetch current prices for holdings to calculate total assets
  useEffect(() => {
    const fetchHoldingsValue = async () => {
      if (holdings.length === 0) { setStocksValue(0); return; }
      try {
        const tickers = holdings.map((h) => h.ticker);
        const data = await getPrices(tickers);
        const total = holdings.reduce((sum, h) => {
          const priceData = (data.tickers || []).find((t) => t.ticker === h.ticker);
          return sum + (priceData?.price || h.avg_price) * h.shares;
        }, 0);
        setStocksValue(total);
      } catch {
        const total = holdings.reduce((sum, h) => sum + h.avg_price * h.shares, 0);
        setStocksValue(total);
      }
    };
    fetchHoldingsValue();
    const interval = setInterval(fetchHoldingsValue, 60000);
    return () => clearInterval(interval);
  }, [holdings]);

  const loadUserData = useCallback(async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (profile) {
      setBalance(profile.balance);
    } else {
      await supabase.from('profiles').insert({ id: user.id, balance: 1000 });
      await supabase.from('portfolio_snapshots').insert({
        user_id: user.id, total_value: 1000, cash: 1000, stocks_value: 0,
      });
    }

    const { data: holdingsData } = await supabase
      .from('holdings')
      .select('*')
      .eq('user_id', user.id);
    setHoldings(holdingsData || []);

    const { data: watchlistData } = await supabase
      .from('watchlist')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    setWatchlist(watchlistData || []);
  }, [user.id]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleTrade = async (ticker, shares, price, type) => {
    const total = shares * price;

    if (type === 'buy') {
      if (total > balance) return { error: 'Insufficient funds' };

      const newBalance = balance - total;
      await supabase.from('profiles').update({ balance: newBalance }).eq('id', user.id);

      const existing = holdings.find((h) => h.ticker === ticker);
      if (existing) {
        const newShares = existing.shares + shares;
        const newAvgPrice = ((existing.avg_price * existing.shares) + total) / newShares;
        await supabase.from('holdings').update({ shares: newShares, avg_price: newAvgPrice }).eq('id', existing.id);
      } else {
        await supabase.from('holdings').insert({ user_id: user.id, ticker, shares, avg_price: price });
      }

      await supabase.from('transactions').insert({ user_id: user.id, ticker, shares, price, type: 'buy' });
      setBalance(newBalance);
    } else {
      const existing = holdings.find((h) => h.ticker === ticker);
      if (!existing || existing.shares < shares) return { error: 'Insufficient shares' };

      const newBalance = balance + total;
      await supabase.from('profiles').update({ balance: newBalance }).eq('id', user.id);

      const newShares = existing.shares - shares;
      if (newShares === 0) {
        await supabase.from('holdings').delete().eq('id', existing.id);
      } else {
        await supabase.from('holdings').update({ shares: newShares }).eq('id', existing.id);
      }

      await supabase.from('transactions').insert({ user_id: user.id, ticker, shares, price, type: 'sell' });
      setBalance(newBalance);
    }

    await loadUserData();

    // Record portfolio snapshot after trade
    try {
      const { data: updatedHoldings } = await supabase
        .from('holdings').select('*').eq('user_id', user.id);
      const newBal = type === 'buy' ? balance - total : balance + total;
      let sv = 0;
      if (updatedHoldings && updatedHoldings.length > 0) {
        const tickerList = updatedHoldings.map(h => h.ticker);
        const priceData = await getPrices(tickerList);
        sv = updatedHoldings.reduce((sum, h) => {
          const p = (priceData.tickers || []).find(t => t.ticker === h.ticker);
          return sum + (p?.price || h.avg_price) * h.shares;
        }, 0);
      }
      await supabase.from('portfolio_snapshots').insert({
        user_id: user.id,
        total_value: newBal + sv,
        cash: newBal,
        stocks_value: sv,
      });
    } catch {}

    return { success: true };
  };

  const addToWatchlist = async (ticker, name) => {
    if (watchlist.find((w) => w.ticker === ticker)) return;
    await supabase.from('watchlist').insert({ user_id: user.id, ticker, name });
    await loadUserData();
  };

  const handleCash = async (mode, amount) => {
    const newBalance = mode === 'deposit' ? balance + amount : balance - amount;
    await supabase.from('profiles').update({ balance: newBalance }).eq('id', user.id);
    setBalance(newBalance);

    // Record transaction
    await supabase.from('transactions').insert({
      user_id: user.id, type: mode, price: amount,
    });

    // Record snapshot
    try {
      await supabase.from('portfolio_snapshots').insert({
        user_id: user.id,
        total_value: newBalance + stocksValue,
        cash: newBalance,
        stocks_value: stocksValue,
      });
    } catch {}

    return { success: true };
  };

  const removeFromWatchlist = async (ticker) => {
    await supabase.from('watchlist').delete().eq('user_id', user.id).eq('ticker', ticker);
    await loadUserData();
  };

  return (
    <div className="dashboard">
      <Navbar balance={balance} totalAssets={balance + stocksValue} onPortfolioClick={() => { setActiveTab('portfolio'); setSelectedStock(null); }} onCashClick={() => setShowCashModal(true)} />
      <div className="dashboard-content">
        <div className="dashboard-sidebar">
          <StockSearch onSelect={setSelectedStock} onAdd={addToWatchlist} />
          <div className="sidebar-tabs">
            <button className={activeTab === 'watchlist' ? 'active' : ''} onClick={() => setActiveTab('watchlist')}>
              Watchlist
            </button>
            <button className={activeTab === 'portfolio' ? 'active' : ''} onClick={() => setActiveTab('portfolio')}>
              Portfolio
            </button>
            <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
              History
            </button>
          </div>
          {activeTab === 'watchlist' ? (
            <Watchlist items={watchlist} onSelect={setSelectedStock} onRemove={removeFromWatchlist} />
          ) : activeTab === 'portfolio' ? (
            <Portfolio holdings={holdings} onSelect={setSelectedStock} />
          ) : (
            <TransactionHistory onSelect={setSelectedStock} />
          )}
        </div>
        <div className="dashboard-main">
          {selectedStock ? (
            <StockChart ticker={selectedStock.ticker} name={selectedStock.name} holdings={holdings} stocksValue={stocksValue}>
              <TradePanel
                ticker={selectedStock.ticker}
                balance={balance}
                holdings={holdings}
                onTrade={handleTrade}
              />
            </StockChart>
          ) : activeTab === 'portfolio' ? (
            <PortfolioChart holdings={holdings} balance={balance} />
          ) : (
            <div className="empty-state">
              <h2>Welcome to VTrade</h2>
              <p>Search for a stock or select one from your watchlist to get started.</p>
            </div>
          )}
        </div>
      </div>
      {showCashModal && (
        <CashModal balance={balance} onSubmit={handleCash} onClose={() => setShowCashModal(false)} />
      )}
    </div>
  );
}
